import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { loadUserSettings, saveUserSettings, migrateSettingsFromLocalStorage } from '../lib/userSettings';
import LoadingIcon from './LoadingIcon';

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function AnalyticsPage({ transactions = [], onDateClick, autoOpenTracker = false, onTrackerOpened }) {
  const { user } = useAuth();
  const [loadingSettings, setLoadingSettings] = useState(true);
  
  // State for target goal and period
  const [target, setTarget] = useState(5000);
  const [showModal, setShowModal] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const [period, setPeriod] = useState('month'); // 'week', '2weeks', '3weeks', 'month'
  const [startDate, setStartDate] = useState(() => {
    // Default to today in YYYY-MM-DD format
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  const [startDateInput, setStartDateInput] = useState('');
  const [activityView, setActivityView] = useState('monthly');
  const [showTrackerDetail, setShowTrackerDetail] = useState(autoOpenTracker);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Handle autoOpenTracker prop change
  useEffect(() => {
    if (autoOpenTracker) {
      setShowTrackerDetail(true);
      if (onTrackerOpened) {
        onTrackerOpened();
      }
    }
  }, [autoOpenTracker, onTrackerOpened]);
  
  // State for calendar month navigation
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);
  const [displayMonth, setDisplayMonth] = useState(todayDate.getMonth());
  const [displayYear, setDisplayYear] = useState(todayDate.getFullYear());
  const [showTrackerHint, setShowTrackerHint] = useState(true);
  
  // Load settings from Supabase on mount
  useEffect(() => {
    const loadSettings = async () => {
      if (user) {
        // Migrate from localStorage first
        await migrateSettingsFromLocalStorage(user.id);
        
        // Load from Supabase
        const settings = await loadUserSettings(user.id);
        if (settings) {
          if (settings.analytics_target) setTarget(parseFloat(settings.analytics_target));
          if (settings.analytics_period) setPeriod(settings.analytics_period);
          if (settings.analytics_start_date) setStartDate(settings.analytics_start_date);
        }
        setLoadingSettings(false);
      } else {
        // Fallback to localStorage if not logged in
        const savedTarget = localStorage.getItem('chatty_wallet_target');
        const savedPeriod = localStorage.getItem('chatty_wallet_period');
        const savedStartDate = localStorage.getItem('chatty_wallet_start_date');
        if (savedTarget) setTarget(parseFloat(savedTarget));
        if (savedPeriod) setPeriod(savedPeriod);
        if (savedStartDate) setStartDate(savedStartDate);
        setLoadingSettings(false);
      }
    };
    
    loadSettings();
  }, [user]);
  
  // Period configuration
  const periodConfig = {
    week: { days: 7, label: '1 Week' },
    '2weeks': { days: 14, label: '2 Weeks' },
    '3weeks': { days: 21, label: '3 Weeks' },
    month: { days: 30, label: '1 Month' }
  };
  
  const currentPeriod = periodConfig[period];
  const daysInPeriod = currentPeriod.days;
  
  // Calculate actual data from transactions
  const expenses = transactions.filter(t => t.type === 'expense');
  const incomes = transactions.filter(t => t.type === 'income');
  // totalExpenses는 전체 지출 (다른 용도로 사용 가능)
  const totalExpenses = expenses.reduce((sum, t) => sum + Math.abs(t.amount), 0);
  
  // Calculate progress bar color based on spending percentage
  // spendingPercentage = 지출한 금액 비율 (높을수록 많이 씀 = 나쁨)
  // 0-60%: Green (점점 진해지는 초록) - 적게 쓸 때
  // 60-80%: Yellow to Orange (노란색에서 주황색으로)
  // 80-100%: Orange to Red (점점 빨갛게, 100%가 제일 빨강) - 많이 쓸 때
  const progressGradientStops = useMemo(() => ({
    start: '#F35DC8',
    end: '#F35DC8'
  }), []);

  const formatCurrency = (value) =>
    value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const parseExpenseDate = (dateStr) => {
    if (!dateStr) return null;

    // Try YYYY-MM-DD format first (most reliable, avoids timezone issues)
    const yyyyMMddMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (yyyyMMddMatch) {
      const year = parseInt(yyyyMMddMatch[1], 10);
      const month = parseInt(yyyyMMddMatch[2], 10) - 1; // 0-indexed
      const day = parseInt(yyyyMMddMatch[3], 10);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        const parsedDate = new Date(year, month, day);
        parsedDate.setHours(0, 0, 0, 0);
        return parsedDate;
      }
    }

    // Fallback to other formats
    const hasFourDigitYear = /\d{4}/.test(dateStr);
    const directParse = hasFourDigitYear ? new Date(dateStr) : null;
    if (directParse && !isNaN(directParse)) {
      directParse.setHours(0, 0, 0, 0);
      return directParse;
    }

    const monthMatch = dateStr.match(/([A-Za-z]+)\s+(\d{1,2})/);
    if (monthMatch) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIndex = monthNames.findIndex(
        (name) => name.toLowerCase() === monthMatch[1].slice(0, 3).toLowerCase()
      );
      if (monthIndex !== -1) {
        const day = parseInt(monthMatch[2], 10);
        const reference = new Date();
        const currentYear = reference.getFullYear();
        const currentMonth = reference.getMonth();
        let year = currentYear;
        if (monthIndex > currentMonth) {
          year -= 1;
        }
        const parsedDate = new Date(year, monthIndex, day);
        parsedDate.setHours(0, 0, 0, 0);
        return parsedDate;
      }
    }

    const slashParts = dateStr.split('/');
    if (slashParts.length >= 2) {
      const month = parseInt(slashParts[0], 10) - 1;
      const day = parseInt(slashParts[1], 10);
      let year = slashParts.length >= 3 ? parseInt(slashParts[2], 10) : new Date().getFullYear();
      if (!isNaN(month) && !isNaN(day)) {
        if (slashParts.length < 3) {
          const reference = new Date();
          const currentMonth = reference.getMonth();
          if (month > currentMonth) {
            year -= 1;
          }
        } else if (!isNaN(year) && year < 100) {
          year += year >= 70 ? 1900 : 2000;
        }
        const parsedDate = new Date(year, month, day);
        parsedDate.setHours(0, 0, 0, 0);
        return parsedDate;
      }
    }

    return null;
  };

  const monthlyTotalsMap = {};

  expenses.forEach((expense) => {
    const expenseDate = parseExpenseDate(expense.date);
    if (!expenseDate) return;

    const monthKey = `${expenseDate.getFullYear()}-${String(expenseDate.getMonth() + 1).padStart(2, '0')}`;
    if (!monthlyTotalsMap[monthKey]) {
      monthlyTotalsMap[monthKey] = {
        total: 0,
        date: new Date(expenseDate.getFullYear(), expenseDate.getMonth(), 1),
      };
    }
    monthlyTotalsMap[monthKey].total += Math.abs(expense.amount);
  });

  const monthlyData = Object.entries(monthlyTotalsMap)
    .map(([key, value]) => ({
      key,
      total: value.total,
      date: value.date,
      label: value.date.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
    }))
    .sort((a, b) => b.date - a.date);

  const monthlyMap = useMemo(() => new Map(monthlyData.map((item) => [item.key, item])), [monthlyData]);
  const formatYearMonthKey = (year, month) => `${year}-${String(month + 1).padStart(2, '0')}`;

  const getMonthlyEntry = useMemo(
    () =>
      (year, month) => {
        let adjustedYear = year;
        let adjustedMonth = month;

        while (adjustedMonth < 0) {
          adjustedYear -= 1;
          adjustedMonth += 12;
        }

        while (adjustedMonth > 11) {
          adjustedYear += 1;
          adjustedMonth -= 12;
        }

        const key = formatYearMonthKey(adjustedYear, adjustedMonth);
        const existing = monthlyMap.get(key);
        if (existing) {
          return existing;
        }

        const date = new Date(adjustedYear, adjustedMonth, 1);
        return {
          key,
          total: 0,
          date,
          label: date.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
        };
      },
    [monthlyMap]
  );

  const currentDateReference = useMemo(() => new Date(), []);
  const defaultYear = currentDateReference.getFullYear();
  const defaultMonth = currentDateReference.getMonth();

  const yearsAvailable = useMemo(() => {
    const years = Array.from(new Set(monthlyData.map((item) => item.date.getFullYear()))).sort((a, b) => b - a);
    if (years.length === 0) {
      return [defaultYear];
    }
    return years;
  }, [monthlyData, defaultYear]);

  const [selectedYear, setSelectedYear] = useState(yearsAvailable[0]);
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(() => {
    if (monthlyData[0]) {
      return monthlyData[0].date.getMonth();
    }
    return defaultMonth;
  });

  // Allow navigation to any year, not just those with data
  // useEffect(() => {
  //   if (!yearsAvailable.includes(selectedYear) && yearsAvailable.length > 0) {
  //     setSelectedYear(yearsAvailable[0]);
  //   }
  // }, [yearsAvailable, selectedYear]);

  const monthOptions = useMemo(() => {
    // Always show all 12 months (0-11: January to December)
    const options = [];
    for (let month = 0; month <= 11; month += 1) {
      options.push(month);
    }
    return options;
  }, []);

  useEffect(() => {
    if (monthOptions.length === 0) {
      return;
    }
    if (!monthOptions.includes(selectedMonthIndex)) {
      setSelectedMonthIndex(monthOptions[0]);
    }
  }, [monthOptions, selectedMonthIndex]);

  const maxMonthlyTotal = monthlyData.reduce((max, item) => Math.max(max, item.total), 0);

  // Get today's date for comparisons
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Parse the selected start date (for goal calculation only)
  // Parse YYYY-MM-DD format to avoid timezone issues
  const startDateParts = startDate.split('-');
  const selectedStartDate = new Date(
    parseInt(startDateParts[0]), // year
    parseInt(startDateParts[1]) - 1, // month (0-indexed)
    parseInt(startDateParts[2]) // day
  );
  selectedStartDate.setHours(0, 0, 0, 0);
  
  // Calculate the end date based on period (starting from selected start date) - for progress display
  const calendarStartDate = new Date(selectedStartDate);
  calendarStartDate.setHours(0, 0, 0, 0);
  const calendarEndDate = new Date(selectedStartDate);
  calendarEndDate.setDate(selectedStartDate.getDate() + daysInPeriod - 1);
  calendarEndDate.setHours(23, 59, 59, 999); // Set to end of day for inclusive comparison

  // Calculate actual spending up to today within the goal period
  const todayTimestamp = today.getTime();
  const startTimestamp = selectedStartDate.getTime();
  const endTimestamp = calendarEndDate.getTime();
  
  // Calculate spending from start date to today (inclusive) within the goal period
  let actualSpendingToDate = 0;
  expenses.forEach(expense => {
    if (expense.date) {
      const expenseDate = parseExpenseDate(expense.date);
      
      // If expense date is within goal period and up to today, add to actual spending
      if (expenseDate) {
        const expenseTimestamp = expenseDate.getTime();
        if (expenseTimestamp >= startTimestamp && expenseTimestamp <= todayTimestamp && expenseTimestamp <= endTimestamp) {
          actualSpendingToDate += Math.abs(expense.amount);
        }
      }
    }
  });

  // Calculate remaining budget and days
  const remainingBudget = Math.max(0, target - actualSpendingToDate);
  const remainingDays = Math.max(1, Math.ceil((endTimestamp - todayTimestamp) / (1000 * 60 * 60 * 24)) + 1); // +1 to include today
  
  // Calculate spending percentage based on actual spending in goal period
  const saved = Math.max(0, target - actualSpendingToDate);
  const spendingPercentage = target > 0 ? Math.round((actualSpendingToDate / target) * 100) : 0;
  const savedPercentage = target > 0 ? Math.round((saved / target) * 100) : 0;
  
  // Calculate dynamic daily goal: remaining budget divided by remaining days
  // If we're past the end date, use the original calculation
  const dailyGoal = todayTimestamp > endTimestamp 
    ? Math.round(target / daysInPeriod)
    : Math.round(remainingBudget / remainingDays);

  // Static daily goal for history consistency (Target / Total Days)
  // This ensures that past days' colors don't change as the remaining budget fluctuates
  const staticDailyGoal = Math.round(target / daysInPeriod);
  
  // Generate array of dates for DISPLAYED MONTH (can navigate to previous/next months)
  const currentMonth = displayMonth;
  const currentYear = displayYear;
  const currentMonthLabel = new Date(currentYear, currentMonth).toLocaleString('en-US', { month: 'long' });
  const daysInCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  const trackerYearOptions = useMemo(() => {
    const yearSet = new Set([displayYear, defaultYear]);
    yearsAvailable.forEach((year) => yearSet.add(year));
    yearSet.add(displayYear - 1);
    yearSet.add(displayYear + 1);
    return Array.from(yearSet).sort((a, b) => a - b);
  }, [displayYear, defaultYear, yearsAvailable]);
  
  const dateArray = [];
  for (let day = 1; day <= daysInCurrentMonth; day++) {
    const date = new Date(currentYear, currentMonth, day);
    date.setHours(0, 0, 0, 0); // Ensure time is set to midnight
    dateArray.push(date);
  }
  
  // Group expenses by actual date (YYYY-MM-DD format for matching)
  const expensesByDate = {};
  expenses.forEach(expense => {
    if (expense.date) {
      // Try to parse the date from various formats
      let expenseDate = null;
      
      // Format like "Nov 4" or "Nov 04"
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dateMatch = expense.date.match(/(\w+)\s+(\d+)/);
      if (dateMatch) {
        const monthName = dateMatch[1];
        const day = parseInt(dateMatch[2]);
        const monthIndex = monthNames.findIndex(m => m === monthName);
        if (monthIndex !== -1) {
          // Match expenses from any month, but prioritize the displayed month/year
          // Try to determine the year - if month is in the past relative to today, use current year
          // Otherwise, check if it matches the displayed month
          let expenseYear = currentYear;
          if (monthIndex === currentMonth) {
            expenseYear = currentYear;
          } else {
            // For other months, try to infer the year
            const todayMonth = new Date().getMonth();
            const todayYear = new Date().getFullYear();
            if (monthIndex <= todayMonth) {
              expenseYear = currentYear; // Same year
            } else {
              expenseYear = currentYear - 1; // Previous year
            }
          }
          expenseDate = new Date(expenseYear, monthIndex, day);
          expenseDate.setHours(0, 0, 0, 0);
        }
      }
      
      // Also try MM/DD format (e.g., "11/07" or "11/7")
      if (!expenseDate) {
        const mmddMatch = expense.date.match(/(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?/);
        if (mmddMatch) {
          const month = parseInt(mmddMatch[1]) - 1; // 0-indexed
          const day = parseInt(mmddMatch[2]);
          const year = mmddMatch[3] ? parseInt(mmddMatch[3]) : currentYear;
          expenseDate = new Date(year, month, day);
          expenseDate.setHours(0, 0, 0, 0);
        }
      }
      
      // If we have a date, format it as YYYY-MM-DD for matching
      // Only include expenses from the displayed month/year
      if (expenseDate && expenseDate.getMonth() === currentMonth && expenseDate.getFullYear() === currentYear) {
        const dateKey = `${expenseDate.getFullYear()}-${String(expenseDate.getMonth() + 1).padStart(2, '0')}-${String(expenseDate.getDate()).padStart(2, '0')}`;
        if (!expensesByDate[dateKey]) {
          expensesByDate[dateKey] = 0;
        }
        expensesByDate[dateKey] += Math.abs(expense.amount);
        
        // Debug for Nov 7
        if (dateKey === `${currentYear}-11-07` && currentMonth === 10) {
          console.log('Nov 7 expense found:', {
            originalDate: expense.date,
            parsedDate: dateKey,
            amount: expense.amount,
            totalForDate: expensesByDate[dateKey],
            currentYear,
            currentMonth
          });
        }
      } else {
        // Debug: log if date couldn't be parsed
        if (expense.date.includes('Nov') || expense.date.includes('7')) {
          console.log('Failed to parse date:', expense.date);
        }
      }
    }
  });

  // Group incomes by actual date (YYYY-MM-DD format for matching)
  const incomesByDate = {};
  incomes.forEach(income => {
    if (income.date) {
      let incomeDate = null;

      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dateMatch = income.date.match(/(\w+)\s+(\d+)/);
      if (dateMatch) {
        const monthName = dateMatch[1];
        const day = parseInt(dateMatch[2], 10);
        const monthIndex = monthNames.findIndex(m => m === monthName);
        if (monthIndex !== -1) {
          let incomeYear = currentYear;
          if (monthIndex === currentMonth) {
            incomeYear = currentYear;
          } else {
            const todayMonth = new Date().getMonth();
            const todayYear = new Date().getFullYear();
            if (monthIndex <= todayMonth) {
              incomeYear = todayYear;
            } else {
              incomeYear = todayYear - 1;
            }
          }
          incomeDate = new Date(incomeYear, monthIndex, day);
          incomeDate.setHours(0, 0, 0, 0);
        }
      }

      if (!incomeDate) {
        const mmddMatch = income.date.match(/(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?/);
        if (mmddMatch) {
          const month = parseInt(mmddMatch[1], 10) - 1;
          const day = parseInt(mmddMatch[2], 10);
          const year = mmddMatch[3] ? parseInt(mmddMatch[3], 10) : currentYear;
          incomeDate = new Date(year, month, day);
          incomeDate.setHours(0, 0, 0, 0);
        }
      }

      if (incomeDate && incomeDate.getMonth() === currentMonth && incomeDate.getFullYear() === currentYear) {
        const dateKey = `${incomeDate.getFullYear()}-${String(incomeDate.getMonth() + 1).padStart(2, '0')}-${String(incomeDate.getDate()).padStart(2, '0')}`;
        if (!incomesByDate[dateKey]) {
          incomesByDate[dateKey] = 0;
        }
        incomesByDate[dateKey] += Math.abs(income.amount);
      }
    }
  });
  
  // Activity data based on actual spending and dates
  const activityData = {};
  
  dateArray.forEach((date, index) => {
    const startTimestamp = selectedStartDate.getTime();
    const endTimestamp = calendarEndDate.getTime();
    const todayTimestamp = today.getTime();
    const dayNumber = date.getDate();
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(dayNumber).padStart(2, '0')}`;
    const daySpending = expensesByDate[dateKey] || 0;
    
    // Compare dates by timestamp to ensure accurate comparison
    const dateTimestamp = date.getTime();
    
    // Check if the date is within the goal period (from selected start date)
    const isInGoalPeriod = dateTimestamp >= startTimestamp && dateTimestamp <= endTimestamp;
    
    // Debug for day 7 (index 6) and day 8 (index 7)
    if (index === 6 || index === 7) {
      console.log(`Day ${dayNumber} (index ${index}) calculation:`, {
        dateKey,
        daySpending,
        expensesByDate: expensesByDate[dateKey],
        dateTimestamp,
        startTimestamp,
        endTimestamp,
        todayTimestamp,
        isInGoalPeriod,
        dailyGoal,
        allExpensesByDate: expensesByDate
      });
    }
    
    // Color logic: Show colors for any date with spending, regardless of goal period
    // Future dates are always gray
    if (dateTimestamp > todayTimestamp) {
      // Within goal period but future date
      if (isInGoalPeriod) {
        activityData[index] = 'future';
      } else {
        activityData[index] = 'inactive';
      }
    } else if (daySpending > 0) {
      // Past/Today with spending: Color based on goal
      if (daySpending > staticDailyGoal) {
        activityData[index] = 'exceeded';
      } else {
        activityData[index] = 'good';
      }
    } else {
      // Past/Today with NO spending
      if (isInGoalPeriod) {
        // Within goal period, no spending is good
        activityData[index] = 'good';
      } else {
        // Outside goal period, no spending is inactive
        activityData[index] = 'inactive';
      }
    }
  });

  const goalStreak = (() => {
    if (!staticDailyGoal || staticDailyGoal <= 0) return 0;
    const streakStartDate = new Date(today);
    streakStartDate.setHours(0, 0, 0, 0);
    let streak = 0;
    const cursor = new Date(streakStartDate);
    while (cursor.getTime() >= calendarStartDate.getTime()) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
      const spending = expensesByDate[key] || 0;
      if (spending <= staticDailyGoal) {
        streak += 1;
      } else {
        break;
      }
      cursor.setDate(cursor.getDate() - 1);
    }
    return streak;
  })();
  
  // Get the first day of the month to align calendar properly
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const firstDayWeekday = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Weekday labels (Sunday first)
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Helper functions
  const getColorClass = (day) => {
    const level = activityData[day];
    if (day === 6 || day === 7) { // Debug for day 7 (index 6) and day 8 (index 7)
      console.log(`Day ${day === 6 ? '7' : '8'} (index ${day}) - level:`, level, 'activityData:', activityData[day]);
    }
    switch(level) {
      case 'future': return 'bg-gray-100';
      case 'exceeded': return 'bg-[#F35DC8]';
      case 'good': return 'bg-[#A4F982]';
      case 'inactive': return 'bg-gray-100';
      default: return 'bg-gray-200';
    }
  };
  
  const getBackgroundColor = (level) => {
    switch(level) {
      case 'future': return '#F3F4F6';
      case 'exceeded': return '#F35DC8';
      case 'good': return '#A4F982';
      case 'inactive': return '#E5E7EB';
      default: return '#E5E7EB';
    }
  };
  
  const getTextColor = (day) => {
    const level = activityData[day];
    if (level === 'future' || level === 'inactive') {
      return 'text-gray-400';
    }
    return 'text-black';
  };

  const renderCalendar = () => {
    const days = [];
    
    // Add empty cells for days before the first day of the month (Sunday-based)
    for (let i = 0; i < firstDayWeekday; i++) {
      days.push(
        <div key={`empty-${i}`} className="flex flex-col items-center justify-center">
          <div className="w-10 h-10 rounded-full"></div>
          <div className="mt-2 min-h-[24px]"></div>
        </div>
      );
    }
    
    // Add cells for each day of the month
    dateArray.forEach((date, index) => {
      const dayNumber = date.getDate();
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      // All dates (past, today, and future) are clickable
      // Compare dates using getTime() for accurate comparison
      const isClickable = true;
      
      // Check if date is within goal period for highlight effect
      const dateTimestamp = date.getTime();
      const startTimestamp = selectedStartDate.getTime();
      const endTimestamp = calendarEndDate.getTime();
      const isInGoalPeriod = dateTimestamp >= startTimestamp && dateTimestamp <= endTimestamp;

      const daySpending = expensesByDate[dateKey] || 0;
      const dayIncome = incomesByDate[dateKey] || 0;
      
      const level = activityData[index] || 'inactive';
      const isActiveDay = level === 'good' || level === 'exceeded';
      
      if (dayNumber) {
        // dayStatusMap[dayNumber] = level; // This line is removed as per the edit hint
      }
      const baseColor = getBackgroundColor(level);
      
      days.push(
        <div key={index} className="flex flex-col items-center justify-start relative min-h-[90px]">
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center ${getColorClass(index)} transition-all relative ${isClickable ? 'hover:scale-110 cursor-pointer' : 'cursor-default'} mb-1`}
            style={{
              ...(isActiveDay && {
                border: '2px solid rgba(17, 24, 39, 0.14)',
                boxShadow: '0 0 10px rgba(15, 23, 42, 0.12), inset 0 0 0 1px rgba(255, 255, 255, 0.35)'
              })
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isClickable && onDateClick) {
                console.log('Date clicked:', dateKey, 'isClickable:', isClickable);
                setShowTrackerDetail(false);
                onDateClick(dateKey);
              } else {
                console.log('Date not clickable:', dateKey, 'isClickable:', isClickable, 'onDateClick:', !!onDateClick);
              }
            }}
          >
            <span className={`text-base font-medium ${isInGoalPeriod ? 'text-black' : getTextColor(index)}`}>{dayNumber}</span>
          </div>
          <div className="flex flex-col items-center justify-start gap-0.5 text-[10px] leading-tight text-center w-full">
            {daySpending > 0 && (
              <div className="font-medium w-full px-0.5 truncate" style={{ color: '#F35DC8' }}>
                -${Math.round(daySpending)}
              </div>
            )}
            {dayIncome > 0 && (
              <div className="text-emerald-600 font-medium w-full px-0.5 truncate">
                +${Math.round(dayIncome)}
              </div>
            )}
          </div>
        </div>
      );
    });
    return days;
  };

  const renderTrackerContent = () => (
    <>
      {showTrackerHint && (
        <div className="mb-8 relative">
          <div className="p-4 pr-8 bg-gray-100 rounded-[12px] flex items-start gap-3 relative z-10">
            <span className="text-lg">💡</span>
            <p className="text-sm text-gray-600 leading-snug font-medium">
              Track your spending habits! Green circles mean you stayed within your daily goal, while pink ones indicate overspending.
            </p>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowTrackerHint(false);
              }}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 p-1"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div className="absolute left-8 -bottom-2 w-4 h-4 bg-gray-100 transform rotate-45"></div>
        </div>
      )}

      <div className="mb-6 text-left">
        <div className="flex items-center gap-4 mb-2">
          <div className="relative inline-flex items-center">
            <select
              value={displayYear}
              onChange={(e) => setDisplayYear(parseInt(e.target.value, 10))}
              className="appearance-none bg-transparent pr-6 text-lg font-semibold text-gray-600 focus:outline-none cursor-pointer"
            >
              {trackerYearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
              ▼
            </span>
          </div>
          <div className="relative inline-flex items-center">
            <select
              value={currentMonth}
              onChange={(e) => setDisplayMonth(parseInt(e.target.value, 10))}
              className="appearance-none bg-transparent pr-7 text-2xl font-semibold text-black focus:outline-none cursor-pointer"
            >
              {MONTH_LABELS.map((label, monthIndex) => (
                <option key={label} value={monthIndex}>
                  {label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
              ▼
            </span>
          </div>
        </div>
        <p className="text-base text-gray-600">Daily Goal: ${formatCurrency(Number.isFinite(dailyGoal) ? dailyGoal : 0)}</p>
        <p className="text-base text-gray-600 mt-1">
          Target Period: {dateRange.start} - {dateRange.end}
        </p>
      </div>
      <div className="grid grid-cols-7 gap-3 mb-4">
        {weekDays.map((day, index) => (
          <div key={index} className="text-center text-sm text-gray-500 font-medium">
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-3 mb-6">
        {renderCalendar()}
      </div>
    </>
  );

  // Calculate start and end dates based on period (from selected start date)
  const getDateRange = () => {
    const formatDate = (date) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${date.getDate()} ${months[date.getMonth()]}`;
    };
    
    return {
      start: formatDate(calendarStartDate),
      end: formatDate(calendarEndDate)
    };
  };
  
  const dateRange = getDateRange();

  const handleSetTarget = async () => {
    const newTarget = parseFloat(targetInput);
    const newStartDate = startDateInput || startDate;
    
    if (newTarget && newTarget > 0) {
      setTarget(newTarget);
      setStartDate(newStartDate);
      
      // Save to Supabase if logged in, otherwise localStorage
      if (user) {
        await saveUserSettings(user.id, {
          analytics_target: newTarget,
          analytics_start_date: newStartDate
        });
      } else {
        localStorage.setItem('chatty_wallet_target', newTarget.toString());
        localStorage.setItem('chatty_wallet_start_date', newStartDate);
      }
      
      setShowModal(false);
      setTargetInput('');
      setStartDateInput('');
    }
  };
  
  // Save target, period, and startDate to Supabase whenever they change
  useEffect(() => {
    if (loadingSettings) return; // Don't save during initial load
    
    const saveSettings = async () => {
      if (user) {
        await saveUserSettings(user.id, {
          analytics_target: target,
          analytics_period: period,
          analytics_start_date: startDate
        });
      } else {
        // Fallback to localStorage if not logged in
        localStorage.setItem('chatty_wallet_target', target.toString());
        localStorage.setItem('chatty_wallet_period', period);
        localStorage.setItem('chatty_wallet_start_date', startDate);
      }
    };
    
    saveSettings();
  }, [target, period, startDate, user, loadingSettings]);

  const renderMonthlySpending = () => {
    if (monthOptions.length === 0) {
      return (
        <div className="space-y-4">
          {/* Monthly Header */}
          <div className="flex flex-col items-start gap-2 mb-4">
            <LoadingIcon size={28} strokeWidth={2} />
            <h2 className="text-3xl font-bold text-black">Monthly</h2>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-semibold text-black">No months to display yet.</p>
          </div>
        </div>
      );
    }

    const safeMonthIndex = monthOptions.includes(selectedMonthIndex)
      ? selectedMonthIndex
      : monthOptions[0];
    const selectedEntry = getMonthlyEntry(selectedYear, safeMonthIndex);

    // Get expenses for selected month
    const selectedMonthExpenses = expenses.filter(expense => {
      try {
        const expenseDate = parseExpenseDate(expense.date);
        if (!expenseDate) return false;
        return expenseDate.getMonth() === safeMonthIndex && expenseDate.getFullYear() === selectedYear;
      } catch (e) {
        return false;
      }
    });

    // Group by category
    const categoryTotals = {};
    selectedMonthExpenses.forEach(expense => {
      const category = expense.category || 'other';
      if (!categoryTotals[category]) {
        categoryTotals[category] = 0;
      }
      categoryTotals[category] += Math.abs(expense.amount);
    });

    // Calculate total from actual filtered expenses (more reliable than monthlyTotalsMap)
    const selectedTotal = Object.values(categoryTotals).reduce((sum, amount) => sum + amount, 0);

    // Sort categories by amount (descending)
    const sortedCategories = Object.entries(categoryTotals)
      .map(([category, amount]) => ({
        category,
        amount,
        percentage: selectedTotal > 0 ? (amount / selectedTotal) * 100 : 0
      }))
      .sort((a, b) => b.amount - a.amount);

    // Colors for pie chart
    const categoryColors = {
      shopping: '#FFB5E8',
      food: '#B5E8B5',
      transport: '#B5D4FF',
      entertainment: '#FFE8B5',
      other: '#E5E7EB'
    };

    // Generate pie chart SVG
    const pieSize = 200;
    const radius = pieSize / 2 - 10;
    const centerX = pieSize / 2;
    const centerY = pieSize / 2;
    let currentAngle = -90; // Start from top

    const pieSlices = sortedCategories.map((item, index) => {
      const angle = (item.percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      
      const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
      const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
      const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
      const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);
      
      const largeArcFlag = angle > 180 ? 1 : 0;
      
      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');

      currentAngle += angle;

      return {
        ...item,
        path: pathData,
        color: categoryColors[item.category] || categoryColors.other
      };
    });

    return (
      <div className="space-y-4">
        {/* Monthly Header */}
        <div className="flex flex-col items-start gap-2 mb-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => {
                let newMonth = safeMonthIndex - 1;
                let newYear = selectedYear;
                if (newMonth < 0) {
                  newMonth = 11;
                  newYear -= 1;
                }
                setSelectedMonthIndex(newMonth);
                setSelectedYear(newYear);
              }}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <h2 className="text-3xl font-bold text-black">
              {new Date(selectedYear, safeMonthIndex).toLocaleString('en-US', { month: 'long' })}
            </h2>
            <button 
              onClick={() => {
                let newMonth = safeMonthIndex + 1;
                let newYear = selectedYear;
                if (newMonth > 11) {
                  newMonth = 0;
                  newYear += 1;
                }
                setSelectedMonthIndex(newMonth);
                setSelectedYear(newYear);
              }}
              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
        
        {/* Amount outside black background */}
        <div className="text-left mb-4">
          <p className="text-4xl font-extrabold text-black tracking-tight">
            ${formatCurrency(selectedTotal)}
          </p>
        </div>
        
        <div className="rounded-[16px] px-6 pt-6 pb-6 relative overflow-hidden bg-gray-100">
          {selectedTotal > 0 ? (
            <>
              {/* Pie Chart */}
              <div className="flex justify-center">
                <svg width={pieSize} height={pieSize} viewBox={`0 0 ${pieSize} ${pieSize}`}>
                  {pieSlices.map((slice, index) => (
                    <path
                      key={index}
                      d={slice.path}
                      fill={slice.color}
                      stroke="#ffffff"
                      strokeWidth="2"
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setSelectedCategory(slice.category)}
                    />
                  ))}
                  {/* Donut hole */}
                  <circle cx={centerX} cy={centerY} r={radius * 0.6} fill="#F3F4F6" />
                </svg>
              </div>
            </>
          ) : (
            <div className="bg-gray-200 rounded-lg p-6 text-center">
              <p className="text-sm text-gray-600">
                No spending recorded for this month yet.
              </p>
            </div>
          )}
        </div>

        {selectedTotal > 0 && (
          <>
            {/* Category List */}
            <div className="space-y-2">
              {sortedCategories.map((item, index) => {
                const isSelected = selectedCategory === item.category;
                const categoryColor = categoryColors[item.category] || categoryColors.other;
                return (
                <div
                  key={item.category}
                  className={`flex items-center justify-between rounded-lg p-3 transition-colors cursor-pointer ${
                    isSelected ? '' : 'bg-gray-50'
                  }`}
                  style={isSelected ? { backgroundColor: categoryColor } : {}}
                  onClick={() => setSelectedCategory(isSelected ? null : item.category)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: categoryColors[item.category] || categoryColors.other }}
                    ></div>
                    <span className="text-sm font-medium text-black capitalize">
                      {item.category}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-black">
                      ${formatCurrency(item.amount)}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">
                      ({item.percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
              );
              })}
            </div>
          </>
        )}
      </div>
    );
  };
;

  if (showTrackerDetail) {
    return (
      <>
      <style>{`
        .analytics-scroll-container::-webkit-scrollbar {
          display: none;
        }
        .analytics-scroll-container {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      <div className="analytics-scroll-container h-full overflow-y-auto pb-24">
        {/* Header */}
        <div className="mb-6 pl-6 pr-6 pt-6">
          <button
            type="button"
            onClick={() => setShowTrackerDetail(false)}
            className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors mb-6"
            aria-label="Back to analytics"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L6 10L12 16" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="text-3xl font-bold text-black">Tracker</h1>
        </div>
 
        <div className="px-6">
          {renderTrackerContent()}
        </div>
      </div>
      </>
    );
  }

  return (
    <>
    <style>{`
      .analytics-scroll-container::-webkit-scrollbar {
        display: none;
      }
      .analytics-scroll-container {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
    `}</style>
    <div className="analytics-scroll-container h-full overflow-y-auto p-6 pb-24 space-y-4">
      {/* Header */}
      <div className="-mx-6 -mt-6 px-6 pt-10">
        <div className="flex justify-between items-end">
          <div className="flex flex-col items-start gap-2">
            <h1 className="text-3xl font-bold text-black">Budget Pulse</h1>
          </div>
          <button 
            onClick={() => {
              setStartDateInput(startDate);
              setTargetInput(target.toString());
              setShowModal(true);
            }}
            className="w-10 h-10 rounded-full bg-gray-100 text-black flex items-center justify-center hover:bg-gray-200 transition-colors flex-shrink-0 mb-1"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle cx="6.25" cy="6.25" r="1.75" stroke="currentColor" strokeWidth="1.6" />
              <line x1="8.75" y1="6.25" x2="16" y2="6.25" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              <circle cx="13.75" cy="13.75" r="1.75" stroke="currentColor" strokeWidth="1.6" />
              <line x1="4" y1="13.75" x2="11.25" y2="13.75" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Progress Section */}
      <div 
        className="rounded-[16px] px-6 pt-4 pb-5 relative overflow-hidden bg-white cursor-pointer active:opacity-95 transition-opacity -mt-2"
        onClick={() => {
          setStartDateInput(startDate);
          setTargetInput(target.toString());
          setShowModal(true);
        }}
      >
        <div
          className="relative flex justify-center items-end mb-3 mx-auto"
          style={{ height: '150px', width: '100%', maxWidth: '300px', position: 'relative' }}
        >
          <svg viewBox="0 0 340 180" className="absolute bottom-0 w-full h-auto">
            <defs>
              <pattern
                id="scratchPattern"
                patternUnits="userSpaceOnUse"
                width="12"
                height="12"
                patternTransform="rotate(20)"
              >
                <line
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="12"
                  stroke="#D1D5DB"
                  strokeWidth="2"
                  strokeLinecap="square"
                  opacity="0.7"
                />
              </pattern>
              <mask id="progressMask">
                <path
                  d="M 20 180 A 150 150 0 0 1 320 180"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="28"
                  strokeLinecap="square"
                  strokeDasharray={`${spendingPercentage * 4.71} 471`}
                />
              </mask>
            </defs>
            {/* Gray background for the track */}
            <path
              d="M 20 180 A 150 150 0 0 1 320 180"
              fill="none"
              stroke="#E5E7EB"
              strokeWidth="28"
              strokeLinecap="square"
            />
            <path
              d="M 20 180 A 150 150 0 0 1 320 180"
              fill="none"
              stroke="url(#scratchPattern)"
              strokeWidth="28"
              strokeLinecap="square"
            />
            <path
              d="M 20 180 A 150 150 0 0 1 320 180"
              fill="none"
              stroke="#F35DC8"
              strokeWidth="28"
              strokeLinecap="square"
              mask="url(#progressMask)"
            />
          </svg>
          
          <div
            className="absolute left-1/2 text-center w-full flex flex-col items-center"
            style={{ top: '72%', transform: 'translate(-50%, -50%)' }}
          >
            <p className="text-xs text-gray-500 mb-1">${actualSpendingToDate.toFixed(2)} Spent</p>
            <p className="text-4xl font-bold text-black mb-1">{spendingPercentage}%</p>
            <p className="text-sm text-gray-500">Target • ${target}</p>
          </div>
        </div>
        
        <div
          className="flex justify-between text-xs text-gray-400 mx-auto"
          style={{
            width: '100%',
            maxWidth: '300px',
            paddingLeft: '0',
            paddingRight: '0',
            boxSizing: 'border-box'
          }}
        >
          <div>
            <p className="font-semibold text-black">{dateRange.start}</p>
            <p>Start</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p className="font-semibold text-black">{dateRange.end}</p>
            <p>End</p>
          </div>
        </div>
      </div>

      {/* Tracker Calendar Section */}
      <div className="bg-white rounded-[16px] p-6 shadow-sm -mx-6 -mt-4">
        {renderTrackerContent()}
      </div>

      {/* Activity Section */}
      <div className="bg-white rounded-[16px] p-6 shadow-sm -mx-6 -mt-2">
        {renderMonthlySpending()}
      </div>

      {/* Target Goal Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-80 mx-4">
            <h2 className="text-xl font-bold text-black mb-2" style={{ fontFamily: 'Aldrich, sans-serif' }}>Set Target Goal</h2>
            <p className="text-sm text-gray-600 mb-6" style={{ fontFamily: 'Aldrich, sans-serif' }}>Enter your savings goal and period</p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'Aldrich, sans-serif' }}>
                Period
              </label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white text-black font-normal"
                style={{
                  fontFamily: 'Aldrich, sans-serif',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  paddingRight: '40px',
                  appearance: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="week">1 Week</option>
                <option value="2weeks">2 Weeks</option>
                <option value="3weeks">3 Weeks</option>
                <option value="month">1 Month</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'Aldrich, sans-serif' }}>
                Start Date
              </label>
              <div className="relative">
                <style>{`
                  input[type="date"]::-webkit-calendar-picker-indicator {
                    background: transparent;
                    bottom: 0;
                    color: transparent;
                    cursor: pointer;
                    height: auto;
                    left: 0;
                    position: absolute;
                    right: 0;
                    top: 0;
                    width: auto;
                  }
                `}</style>
                <input
                  type="date"
                  value={startDateInput || startDate}
                  onChange={(e) => setStartDateInput(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black pr-10 text-black"
                  style={{ 
                    fontFamily: 'Aldrich, sans-serif',
                    colorScheme: 'light'
                  }}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                    <line x1="8" y1="14" x2="16" y2="14"></line>
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: 'Aldrich, sans-serif' }}>
                Target Amount ($)
              </label>
              <input
                type="number"
                value={targetInput}
                onChange={(e) => setTargetInput(e.target.value)}
                placeholder="200"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-black"
                style={{ fontFamily: 'Aldrich, sans-serif' }}
              />
              {targetInput && !isNaN(parseFloat(targetInput)) && parseFloat(targetInput) > 0 && (
                <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'Aldrich, sans-serif' }}>
                  Daily Goal: ${Math.round(parseFloat(targetInput) / daysInPeriod)}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setTargetInput('');
                  setStartDateInput('');
                }}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                style={{ fontFamily: 'Aldrich, sans-serif' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSetTarget}
                className="flex-1 px-4 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
                style={{ fontFamily: 'Aldrich, sans-serif' }}
              >
                Set
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </>
  );
}

export default AnalyticsPage;
